import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const clean = (t) => (t || "").replace(/[*_#`>]/g, "").replace(/\s+-\s+/g, ". ");
const ACKS = ["mm-hmm", "okay", "right", "yeah", "got it", "uh-huh"];
const THINK = ["Hmm, okay... let me check.", "Okay, one sec.", "Right, let me see...", "Got it, give me a second.", "Mm, okay, let me look."];
const pick = (arr, not) => { const opts = arr.filter((x) => x !== not); return opts[Math.floor(Math.random() * opts.length)]; };
const SILENCE_MS = 1600;

const pickVoice = () => {
  const voices = window.speechSynthesis?.getVoices() || [];
  return (
    voices.find((v) => /en-GB/i.test(v.lang) && /(daniel|arthur|george|male)/i.test(v.name)) ||
    voices.find((v) => /en-GB/i.test(v.lang)) ||
    voices.find((v) => /^en/i.test(v.lang))
  );
};

const utter = (text, { volume = 1, rate = 1.03 } = {}) => {
  const u = new SpeechSynthesisUtterance(clean(text));
  const v = pickVoice();
  if (v) u.voice = v;
  u.rate = rate;
  u.pitch = 0.9;
  u.volume = volume;
  return u;
};

// Remove the backchannel words JARVIS said that the mic may have picked up
const stripAcks = (text, said) => said.reduceRight((t, w) => t.replace(new RegExp(`(^|\\s)${w}[.,!]?(?=\\s|$)(?!.*\\b${w}\\b)`, "i"), " "), text).replace(/\s+/g, " ").trim();

export function useVoice({ human = true } = {}) {
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const recRef = useRef(null);
  const fillerRef = useRef(false);

  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  const listen = (onText) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser — try Chrome or Safari.");
      return;
    }
    const r = new SR();
    r.lang = "en-US";
    r.interimResults = true;
    r.continuous = true;
    let finalText = "", silence = null, lastAck = 0, lastWord = "", words = 0, done = false;
    const said = [];
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(silence);
      r.stop();
      const text = stripAcks(finalText, said);
      if (!text) return;
      if (human && window.speechSynthesis) {
        fillerRef.current = true;
        const u = utter(pick(THINK), { rate: 1 });
        u.onend = () => { fillerRef.current = false; };
        window.speechSynthesis.speak(u);
      }
      onText(text);
    };
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += ` ${e.results[i][0].transcript}`;
        else interim += e.results[i][0].transcript;
      }
      clearTimeout(silence);
      silence = setTimeout(finish, SILENCE_MS);
      const n = `${finalText} ${interim}`.trim().split(/\s+/).length;
      // Short "mm-hmm / okay" when the speaker pauses mid-thought after a few words
      if (human && e.results[e.results.length - 1].isFinal && n - words >= 5 && Date.now() - lastAck > 4000 && window.speechSynthesis && !window.speechSynthesis.speaking) {
        lastAck = Date.now();
        words = n;
        lastWord = pick(ACKS, lastWord);
        said.push(lastWord.replace("-", "[- ]?"));
        window.speechSynthesis.speak(utter(lastWord, { volume: 0.55, rate: 1.1 }));
      }
    };
    r.onend = () => { setListening(false); if (!done && finalText.trim()) finish(); };
    r.onerror = () => setListening(false);
    recRef.current = r;
    r.start();
    setListening(true);
  };

  const stopListening = () => recRef.current?.stop();

  const speak = (text) => {
    if (!window.speechSynthesis || !text) return;
    if (!fillerRef.current) window.speechSynthesis.cancel(); // let the "hmm, one sec" filler finish naturally
    const u = utter(text);
    u.onstart = () => setSpeaking(true);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
  };

  const hush = () => {
    window.speechSynthesis?.cancel();
    fillerRef.current = false;
    setSpeaking(false);
  };

  return { listening, speaking, listen, stopListening, speak, hush };
}
